import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { t } from "i18next";

import { faFilter, faCloudArrowDown } from "@fortawesome/pro-regular-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PlusIcon } from "@heroicons/react/20/solid";

import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { TableLoader } from "../molecules/TableLoader";
import EllipsesMenu from "../molecules/EllipsesMenu";
import ColumnText from "../molecules/ColumnText";
import PaginationUI from "../atoms/PaginationUI";
import AvatarTable from "../atoms/AvatarTable";
import ToggleUI from "../atoms/ToggleUI";
import { Select } from "./Select";
import { Empty } from "./Empty";

import { QueryMetaData } from "@/app/gyms/gyms";

import { selectUser } from "@/app/auth/auth.slice";

import { Permission, usePermission } from "@/shared/hooks/usePermission";

import { useTagMarketingQuery } from "@/app/users/users.api";

type tagsProps = {
  id: number | string;
  name: string;
  color: string;
};

type InfoCuotaProps = {
  description: string;
  date: string;
};

type UserProps = {
  id: number;
  image: string;
  name: string;
  email: string;
  phone: string;
  state: boolean | number | string;
  typePayment: string;
  infoCouta: InfoCuotaProps;
  coutas: string;
  bonos: string[];
  coutasCursos: string[];
  cursos: string[];
  proxEval: string;
  etiquetasUser: tagsProps[];
  etiquetasMarketing: tagsProps[];
  trainingCreateBy: string;
  nutritionCreateBy: string;

  active: boolean;
  courses: { course: { name: string } }[];
  bonds: { bond: { name: string } }[];
  feesCourses: { fee: { name: string } }[];
  activeProgress: {
    date_end: string;
    plan_table_name: string;
    assigned_by: string;
  };
  activeProgressNutrition: { assigned_by: string };
  stateFee: {
    manual_type: number;
    method: string;
    fee: { name: string };
    status: "active" | "unsubscribe" | "delayed";
    status_aux?: "Payment declined";
    date_finish: string;
    date_init?: string;
  };
  stateBond: { manual_type: number; method: string };
  userTags: string;
  marketingTags: string;
  //userTags: { name: string; color: string }[];
};

type optionsProps = {
  id: number | string;
  name: string;
};

export type TableListUsersProps = {
  users: UserProps[];
  title: string;
  isLoading?: boolean;
  description: string;
  selectedGyms: optionsProps;
  setSelectedGyms: React.Dispatch<React.SetStateAction<optionsProps>>;
  optionsGyms: optionsProps[];
  selectedBranchs: optionsProps;
  setSelectedBranchs: React.Dispatch<React.SetStateAction<optionsProps>>;
  optionsBranchs: optionsProps[];
  selectedStatus: optionsProps;
  setSelectedStatus: React.Dispatch<React.SetStateAction<optionsProps>>;
  optionsStatus: optionsProps[];
  onClickFilters: () => void;
  handlePageChange: (page: number) => void;
  handleEntriesChange: (entries: number) => void;
  entries: number;
  queryMetaData: QueryMetaData;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  functionButtonCreate: () => void;
  exportUsers: () => void;
  functionModalQrCode: () => void;
  functionCopyQrCode: () => void;
  functionRegisterOneDay: () => void;
  handleStateUser: (id: number, state: boolean) => void;
};

const TableListUsers: React.FC<TableListUsersProps> = ({
  title,
  isLoading,
  description,
  users,
  selectedGyms,
  setSelectedGyms,
  optionsGyms,
  selectedBranchs,
  setSelectedBranchs,
  optionsBranchs,
  selectedStatus,
  setSelectedStatus,
  optionsStatus,
  onClickFilters,
  searchQuery,
  onSearchChange,
  handlePageChange,
  handleEntriesChange,
  entries,
  queryMetaData,
  functionButtonCreate,
  exportUsers,
  functionModalQrCode,
  functionCopyQrCode,
  functionRegisterOneDay,
  handleStateUser,
}) => {
  const user = useSelector(selectUser);

  const [branchId, setBranchId] = useState(0);
  const [branchSelected, setBranchSelected] = useState();

  useEffect(() => {
    if (user && user.branch) {
      setBranchId(user.branch.id);
      setBranchSelected(user.branch);
    }
  }, [user]);

  // Nutrition created pby
  const { data: tagsBranchs } = useTagMarketingQuery(
    { branchId: branchId },
    { skip: branchId <= 0 }
  );

  const navigate = useNavigate();
  const { can } = usePermission();

  const optionsMenu = [
    {
      name: t("user.views.code_qr"),
      functionMenu: () => {
        functionModalQrCode();
      },
    },
    {
      name: t("user.views.copy-influencer"),
      functionMenu: () => {
        functionCopyQrCode();
      },
    },
    {
      name: t("user.views.quick_registration"),
      functionMenu: () => {
        functionRegisterOneDay();
      },
    },
  ];

  const renderPaymentManual = (manualType: number) => {
    switch (manualType) {
      case 1:
        return t("user.views.payment_manual_1");
      case 2:
        return t("user.views.payment_manual_2");
      case 3:
        return t("user.views.payment_manual_3");
      case 4:
        return t("user.views.payment_manual_4");
      case 5:
        return t("user.views.payment_manual_5");
      default:
        return null;
    }
  };

  const renderPaymentMethod = (method: string) => {
    switch (method) {
      case "cash":
        return t("user.views.payment_cash");
      case "card":
        return t("user.views.payment_card");
      case "direct_debit":
        return t("user.views.payment_direct_debit");
      case "free":
        return t("user.views.payment_free");
      default:
        return null;
    }
  };

  const formatDate = (date: any) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    return new Date(date)
      .toLocaleDateString("en-GB", options)
      .replace(/\//g, "-");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0"></div>
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      <div className="flex flex-col md:flex-row md:gap-x-2 bottom pb-2">
        <div className="flex flex-col flex-1 gap-4 md:gap-2 md:flex-row md:items-start">
          <div className="sm:flex md:w-1/6">
            <InputWithCornerHint
              label={t("general.search")}
              id="search"
              name="search"
              type="text"
              className="w-full"
              placeholder={t("share.views.name_email_dni")}
              value={searchQuery}
              onChange={onSearchChange}
            />
          </div>
          <div className="col-span-full md:w-1/6">
            <Select
              name="gym"
              tittle={t("gym.views.camera")}
              selected={selectedGyms}
              setSelected={setSelectedGyms}
              options={optionsGyms}
            />
          </div>
          <div className="col-span-full md:w-1/6">
            <Select
              name="branch"
              tittle={t("gym.views.branchs")}
              selected={selectedBranchs}
              setSelected={(branch) => {
                setSelectedBranchs(branch);
              }}
              options={optionsBranchs}
            />
          </div>
          <div className="col-span-full md:w-1/6">
            <Select
              name="status"
              tittle={t("gym.views.status")}
              selected={selectedStatus}
              setSelected={(status) => {
                setSelectedStatus(status);
              }}
              options={optionsStatus}
            />
          </div>
          <div className="flex">
            <div className="col-span-full -mt-3 md:mt-5">
              <button
                type="button"
                className="-ml-2 md:-ml-2 p-2 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">View KPIs</span>
                <FontAwesomeIcon
                  icon={faFilter}
                  aria-hidden="true"
                  className="h-6 w-6"
                  onClick={onClickFilters}
                />
              </button>
            </div>
            <div className="col-span-full -mt-3 md:mt-5">
              <button
                type="button"
                className="-ml-2 md:-ml-3 p-2 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">View KPIs</span>
                <FontAwesomeIcon
                  icon={faCloudArrowDown}
                  aria-hidden="true"
                  className="h-7 w-7"
                  onClick={exportUsers}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="flex md:flex-row md:items-center gap-3 gap-x-2 md:gap-x-2 pt-6 md:pt-0 md:self-end">
          {can(Permission.USERS_CREATE_USERS) && (
            <button
              onClick={functionButtonCreate}
              type="button"
              className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none mb-1"
            >
              <PlusIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          )}
          <div className="mt-1 md:mt-0">
            <EllipsesMenu name="options" options={optionsMenu} />
          </div>
        </div>
      </div>
      <div className="flow-root md:px-5">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 px-3">
            {isLoading ? (
              <TableLoader columns={12} rows={10} />
            ) : users.length === 0 ? (
              <div className="my-5">
                <Empty texto={t("lesson.views.no_users")} />
              </div>
            ) : (
              <Fragment>
                <table className="min-w-full border-t border-gray-300 divide-y divide-gray-300">
                  <thead className="">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("user.views.user")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        {t("user.views.toggle_active")}
                      </th>

                      {user.branch.customerToGymSubscription ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: "80px",
                          }}
                        >
                          {t("user.views.type_pay")}
                        </th>
                      ) : null}
                      {user.branch.customerToGymSubscription ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: "80px",
                          }}
                        >
                          {t("user.views.fee_information")}
                        </th>
                      ) : null}
                      {user.branch.customerToGymSubscription ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: "80px",
                          }}
                        >
                          {t("fees.views.title")}
                        </th>
                      ) : null}
                      {user.branch.customerToGymSubscription ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          Bonos
                        </th>
                      ) : null}

                      {user.branch.courseModule ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            width: "80px",
                          }}
                        >
                          {t("user.views.fee_information")} cursos
                        </th>
                      ) : null}

                      {user.branch.courseModule ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          Cuotas cursos
                        </th>
                      ) : null}

                      {user.branch.courseModule ? (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          Cursos
                        </th>
                      ) : null}

                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          width: "100px",
                        }}
                      >
                        {t("user.views.next_eval_2")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          width: "125px",
                        }}
                      >
                        {t("task.views.labels")} usuario
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          width: "125px",
                        }}
                      >
                        {t("task.views.labels")} marketing
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          width: "125px",
                        }}
                      >
                        {t("user.views.training_created_by")}
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          width: "125px",
                        }}
                      >
                        {t("user.views.nutrition_created_by")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {users.map((user) => {
                      // Convertir tagUsers a un array de IDs
                      const tagUserIds = user.userTags
                        ? user.userTags.split(",").map(Number)
                        : [];

                      const tagMarketingIds = user.marketingTags
                        ? user.marketingTags.split(",").map(Number)
                        : [];

                      // Buscar las etiquetas en tagsBranchs
                      const userTags = tagsBranchs
                        ? tagsBranchs.filter((tag) =>
                            tagUserIds.includes(tag.id)
                          )
                        : [];

                      const marketingTags = tagsBranchs
                        ? tagsBranchs.filter((tag) =>
                            tagMarketingIds.includes(tag.id)
                          )
                        : [];

                      return (
                        <tr key={user.id}>
                          <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
                            <div className="flex items-center">
                              <AvatarTable image={user.image} />
                              <div className="ml-4">
                                <>
                                  <div className="font-medium text-gray-900">
                                    <button
                                      onClick={() =>
                                        window.open(
                                          `/users/${user.id}`,
                                          "_blank"
                                        )
                                      }
                                    >
                                      {user.name}
                                    </button>
                                  </div>
                                  <div className="mt-1 text-gray-500">
                                    <a href={`mailto:${user?.email}`}>
                                      {user.email}
                                    </a>
                                  </div>
                                  <div className="mt-1 text-gray-500">
                                    {user.phone && (
                                      <a
                                        href={`https://wa.me/${user.phone}`}
                                        target="_blank"
                                        className="flex items-center gap-x-1"
                                      >
                                        <FontAwesomeIcon
                                          icon={faWhatsapp}
                                          className="h-4 w-4 text-gray-400"
                                        />
                                        {user.phone}
                                      </a>
                                    )}
                                  </div>
                                </>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            {can(Permission.USERS_ACTIVATE_DISABLE_USERS) && (
                              <ToggleUI
                                state={Boolean(user.active)}
                                disabled={false}
                                onChange={(state) =>
                                  handleStateUser(user.id, state)
                                }
                              />
                            )}
                          </td>

                          {branchSelected &&
                          branchSelected?.customerToGymSubscription ? (
                            <ColumnText
                              title={
                                user.stateFee ? (
                                  <>
                                    {renderPaymentManual(
                                      user.stateFee.manual_type
                                    )}
                                    <br />
                                    {user.stateFee.method &&
                                      `(${renderPaymentMethod(user.stateFee.method)})`}
                                  </>
                                ) : (
                                  user.stateBond && (
                                    <>
                                      {renderPaymentManual(
                                        user.stateBond.manual_type
                                      )}
                                      <br />
                                      {user.stateBond.method &&
                                        `(${renderPaymentMethod(user.stateBond.method)})`}
                                    </>
                                  )
                                )
                              }
                            />
                          ) : null}

                          {branchSelected &&
                          branchSelected?.customerToGymSubscription ? (
                            <td style={{ width: "80px" }}>
                              {user.stateFee && (
                                <>
                                  {user.stateFee.status === "active" && (
                                    <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-green-50 text-green-700 ring-green-600/20">
                                      Activo hasta
                                    </span>
                                  )}
                                  {user.stateFee.status === "unsubscribe" &&
                                    user.stateFee.date_finish >=
                                      new Date().toISOString() && (
                                      <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-gray-50 text-gray-700 ring-gray-600/20">
                                        De baja, activo hasta
                                      </span>
                                    )}
                                  {user.stateFee.status === "unsubscribe" &&
                                    user.stateFee.date_finish <
                                      new Date().toISOString() && (
                                      <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-gray-50 text-gray-700 ring-gray-600/20">
                                        De baja
                                      </span>
                                    )}
                                  {user.stateFee.status === "delayed" && (
                                    <>
                                      <span className="inline-flex justify-start items-center rounded-md px-2 py-1 text-xs font-medium bg-red-50 text-red-700 ring-red-600/20">
                                        Atrasado
                                      </span>
                                      <br />
                                      {user.stateFee.status_aux ===
                                        "Payment declined" && (
                                        <span style={{ fontSize: "10px" }}>
                                          Cobro rechazado
                                        </span>
                                      )}
                                    </>
                                  )}
                                  <br />
                                  <span
                                    className="flex justify-start text-gray-500"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {formatDate(user.stateFee.date_finish)}
                                  </span>
                                </>
                              )}
                            </td>
                          ) : null}

                          {branchSelected &&
                          branchSelected?.customerToGymSubscription ? (
                            <ColumnText title={user.stateFee?.fee?.name} />
                          ) : null}

                          {branchSelected &&
                          branchSelected?.customerToGymSubscription ? (
                            <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                              <ul>
                                {user.bonds?.map((bono, index) => (
                                  <li key={index}>{bono.bond.name}</li>
                                ))}
                              </ul>
                            </td>
                          ) : null}

                          {branchSelected && branchSelected?.courseModule ? (
                            <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                              <ul>
                                {user.feesCourses?.map((cuotasCurso, index) => (
                                  <li key={index}>{cuotasCurso.fee.name}</li>
                                ))}
                              </ul>
                            </td>
                          ) : null}

                          {branchSelected && branchSelected?.courseModule ? (
                            <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                              <ul>
                                {user.courses?.map((curso, index) => (
                                  <li key={index}>{curso.course.name}</li>
                                ))}
                              </ul>
                            </td>
                          ) : null}

                          <ColumnText
                            title={
                              user.activeProgress
                                ? new Date(user.activeProgress?.date_end) <
                                  new Date()
                                  ? t("user.views.timeout")
                                  : Math.floor(
                                      Math.abs(
                                        new Date().getTime() -
                                          new Date(
                                            user.activeProgress?.date_end
                                          ).getTime()
                                      ) /
                                        (1000 * 60 * 60 * 24)
                                    ) + " Days"
                                : ""
                            }
                          />
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <ul>
                              {userTags.map((etiqueta, index) => (
                                <li key={index} className="mb-1">
                                  <span
                                    style={{ backgroundColor: etiqueta.color }}
                                    className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-white"
                                  >
                                    {etiqueta.name}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            <ul>
                              {marketingTags.map((etiqueta, index) => (
                                <li key={index} className="mb-1">
                                  <span
                                    style={{ backgroundColor: etiqueta.color }}
                                    className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-white"
                                  >
                                    {etiqueta.name}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                          <ColumnText
                            title={user.activeProgress?.assigned_by}
                          />
                          <ColumnText
                            title={user.activeProgressNutrition?.assigned_by}
                          />
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Fragment>
            )}
          </div>
        </div>
        <div className="mt-4">
          <PaginationUI
            onPageChange={(event) => handlePageChange(event)}
            currentPage={queryMetaData.page}
            pageSize={queryMetaData.perPage}
            totalResults={queryMetaData.total}
            totalPages={queryMetaData.lastPage}
          />
        </div>
      </div>
    </div>
  );
};

export default TableListUsers;
